import { useState } from 'react';
import { HelpCircle, Send, Mail, Phone, MessageSquare, ExternalLink, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useSiteSettings } from '@/context/SiteSettingsContext';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

const SUPPORT_EMAIL = 'workwith@nyza-studio.at';

export default function AdminSupport() {
  const { settings } = useSiteSettings();
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    contact_email: '',
    contact_phone: '',
    preferred_contact: 'email'
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject || !formData.message || !formData.contact_email) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await api.post('/support/ticket', {
        ...formData,
        website_name: settings?.site_name || 'Unbekannt',
        support_email: SUPPORT_EMAIL
      });
      
      setSuccess(true);
      toast.success('Support-Anfrage erfolgreich gesendet!');
    } catch (err) {
      toast.error('Fehler beim Senden der Anfrage');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <Check className="text-green-500" size={40} />
          </div>
          <h1 className="text-2xl font-heading font-bold mb-4">Anfrage gesendet!</h1>
          <p className="text-muted-foreground mb-6">
            Vielen Dank für Ihre Support-Anfrage. Wir werden uns schnellstmöglich bei Ihnen melden.
          </p>
          <Button onClick={() => { setSuccess(false); setFormData({ subject: '', message: '', contact_email: '', contact_phone: '', preferred_contact: 'email' }); }}>
            Neue Anfrage stellen
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-heading font-bold flex items-center gap-3">
          <HelpCircle className="text-gold" size={28} />
          Support
        </h1>
        <p className="text-muted-foreground mt-1">
          Haben Sie Fragen oder benötigen Sie Hilfe? Kontaktieren Sie unser Support-Team.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contact Form */}
        <div className="lg:col-span-2 bg-card border border-border rounded-lg p-6">
          <h2 className="font-heading font-medium text-lg mb-4 flex items-center gap-2">
            <MessageSquare size={20} />
            Support-Ticket erstellen
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Website Info (auto-filled, read-only) */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong>Website:</strong> {settings?.site_name || 'Wird geladen...'}
              </p>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium mb-2">Betreff *</label>
              <select
                value={formData.subject}
                onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                required
                className="w-full border border-border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Bitte wählen...</option>
                <option value="Technisches Problem">Technisches Problem</option>
                <option value="Frage zur Funktion">Frage zur Funktion</option>
                <option value="Neue Funktion anfragen">Neue Funktion anfragen</option>
                <option value="Abrechnung">Abrechnung</option>
                <option value="Sonstiges">Sonstiges</option>
              </select>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium mb-2">Nachricht *</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                required
                placeholder="Beschreiben Sie Ihr Anliegen ausführlich..."
                className="w-full border border-border rounded-lg px-3 py-2 text-sm min-h-[150px]"
              />
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Mail size={14} className="inline mr-1" />
                  Ihre E-Mail *
                </label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                  required
                  placeholder="ihre@email.de"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Phone size={14} className="inline mr-1" />
                  Ihre Telefonnummer (optional)
                </label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                  placeholder="+43 123 456 789"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            {/* Preferred Contact Method */}
            <div>
              <label className="block text-sm font-medium mb-2">Bevorzugte Kontaktaufnahme</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="preferred_contact"
                    value="email"
                    checked={formData.preferred_contact === 'email'}
                    onChange={(e) => setFormData(prev => ({ ...prev, preferred_contact: e.target.value }))}
                    className="text-primary"
                  />
                  <span className="text-sm">E-Mail</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="preferred_contact"
                    value="phone"
                    checked={formData.preferred_contact === 'phone'}
                    onChange={(e) => setFormData(prev => ({ ...prev, preferred_contact: e.target.value }))}
                    className="text-primary"
                  />
                  <span className="text-sm">Telefon</span>
                </label>
              </div>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-gold hover:bg-gold/90 text-black gap-2"
            >
              <Send size={16} />
              {submitting ? 'Wird gesendet...' : 'Anfrage senden'}
            </Button>
          </form>
        </div>

        {/* Contact Info Sidebar */}
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="font-heading font-medium mb-4">Direkter Kontakt</h3>
            
            <div className="space-y-3">
              <a 
                href={`mailto:${SUPPORT_EMAIL}`}
                className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
              >
                <Mail size={18} className="text-gold" />
                <div>
                  <p className="text-sm font-medium">E-Mail</p>
                  <p className="text-xs text-muted-foreground">{SUPPORT_EMAIL}</p>
                </div>
                <ExternalLink size={14} className="ml-auto text-muted-foreground" />
              </a>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="font-heading font-medium mb-4">Hilfreiche Links</h3>
            
            <div className="space-y-2">
              <a 
                href="https://nyza-studio.at"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <ExternalLink size={14} />
                NYZA-STUDIO Website
              </a>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>Hinweis:</strong> Unsere Support-Zeiten sind Montag bis Freitag, 9:00 - 18:00 Uhr (MEZ).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
